// Vale de venta (0109, Rodrigo 2026-10-24; como el de BotiOS): tercera opción del punto de venta,
// junto a boleta y factura. Se comporta igual en todo lo comercial (stock, caja, cuenta corriente),
// pero NO es un documento tributario: no lleva timbre, no usa folios del SII y no se envía. Aquí: su
// impresión (sin timbre, con su número y motivo) y la consulta de los vales emitidos.
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { MOTIVOS_VALE } from '../api/contratos';
import { PanelError } from '../comun/Estados';
import { fecha, fechaHora, pesos, rut as formatoRut } from '../comun/formato';
import { EncabezadoImpreso, Imprimir } from '../comun/Imprimir';
import { useSesion } from '../sesion/SesionContexto';

export interface Vale {
  id: string;
  numero: string;
  notaId: string;
  notaNumeroVisible: string;
  fechaEmision: string;
  motivo: string;
  neto: string;
  iva: string;
  total: string;
  condicionPago: string;
  clienteRut: string | null;
  clienteRazonSocial: string;
  creadoEn: string;
  /** 0121: anulado (volvió la mercadería): no cuenta como venta. */
  anulado?: boolean;
  contenido: {
    lineas: Array<{
      codigo: string;
      nombre: string;
      cantidad: string;
      unidad: string;
      bruto: string;
    }>;
    impresion?: {
      vendedor?: string | null;
      condicion?: string | null;
      observaciones?: string | null;
    };
  };
}

export const nombreMotivoVale = (m: string) => MOTIVOS_VALE.find(([v]) => v === m)?.[1] ?? m;

const rutVisible = (r: string | null) => {
  if (!r) return '—';
  const [n, dv] = r.includes('-') ? r.split('-') : [r.slice(0, -1), r.slice(-1)];
  return formatoRut(n ?? '', dv ?? '');
};

/** El vale en papel: encabezado, cliente, detalle y total. Sin timbre (no es DTE). */
export function ValeImpreso({ vale, empresa }: { vale: Vale; empresa: string }) {
  return (
    <article className="impreso vale-impreso" data-vale={vale.numero}>
      <EncabezadoImpreso
        empresa={empresa}
        titulo="Vale de venta · documento interno, no tributario"
        numero={`VALE N° ${vale.numero}${vale.anulado ? ' · ANULADO' : ''}`}
        detalle={
          <dl className="impreso-datos">
            <dt>Cliente</dt>
            <dd>
              <b>{vale.clienteRazonSocial}</b> {rutVisible(vale.clienteRut)}
            </dd>
            <dt>Fecha</dt>
            <dd>{fecha(vale.fechaEmision)}</dd>
            <dt>Motivo</dt>
            <dd>{nombreMotivoVale(vale.motivo)}</dd>
            <dt>Nota de venta</dt>
            <dd>{vale.notaNumeroVisible}</dd>
            {vale.contenido.impresion?.vendedor && (
              <>
                <dt>Vendedor</dt>
                <dd>{vale.contenido.impresion.vendedor}</dd>
              </>
            )}
            {vale.contenido.impresion?.condicion && (
              <>
                <dt>Condición</dt>
                <dd>{vale.contenido.impresion.condicion}</dd>
              </>
            )}
          </dl>
        }
      />
      <table className="impreso-tabla">
        <thead>
          <tr>
            <th>Código</th>
            <th>Producto</th>
            <th className="der">Cantidad</th>
            <th className="der">Total</th>
          </tr>
        </thead>
        <tbody>
          {vale.contenido.lineas.map((l, i) => (
            <tr key={i}>
              <td className="mono">{l.codigo}</td>
              <td>{l.nombre}</td>
              <td className="der mono">
                {l.cantidad} {l.unidad}
              </td>
              <td className="der mono">{pesos(l.bruto)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} className="der">
              <b>TOTAL</b>
            </td>
            <td className="der mono">
              <b>{pesos(vale.total)}</b>
            </td>
          </tr>
        </tfoot>
      </table>
      {vale.contenido.impresion?.observaciones && (
        <p>Observación: {vale.contenido.impresion.observaciones}</p>
      )}
      <p className="vale-leyenda">
        Este vale no es una boleta ni una factura: no tiene validez tributaria.
      </p>
    </article>
  );
}

/** Trae el vale y lo imprime (al terminar, llama `alTerminar`). */
export function ImprimirVale({ valeId, alTerminar }: { valeId: string; alTerminar: () => void }) {
  const { empresa } = useSesion();
  const q = useQuery({
    queryKey: ['vale', valeId],
    queryFn: () => api.get<Vale>(`/ventas/vales/${valeId}`),
  });
  if (!q.data) return null;
  return (
    <Imprimir alTerminar={alTerminar}>
      <ValeImpreso vale={q.data} empresa={empresa!.razon_social} />
    </Imprimir>
  );
}

/** Consulta de vales de venta emitidos: filtro por fechas o número, reimpresión y su nota. */
export function ConsultaVales({
  alAbrirNota,
}: {
  alAbrirNota: (notaId: string, titulo: string) => void;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const hoy = new Date().toISOString().slice(0, 10);
  const [desde, setDesde] = useState(hoy.slice(0, 8) + '01');
  const [hasta, setHasta] = useState(hoy);
  const [numero, setNumero] = useState('');
  const [imprimir, setImprimir] = useState<string | null>(null);
  const q = useQuery({
    queryKey: ['emp', empresaId, 'vales', desde, hasta, numero],
    queryFn: () =>
      api.get<Omit<Vale, 'contenido' | 'neto' | 'iva' | 'condicionPago'>[]>('/ventas/vales', {
        query: {
          empresaId,
          ...(numero ? { numero } : { desde, hasta }),
        },
      }),
  });
  const filas = q.data ?? [];
  const vigentes = filas.filter((v) => !v.anulado);
  const total = vigentes.reduce((s, v) => s + Number(v.total), 0);
  return (
    <div className="pantalla consulta" data-pantalla="vales">
      <div className="barra-filtros">
        <label className="campo">
          <span className="etiqueta">Desde</span>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label className="campo">
          <span className="etiqueta">Hasta</span>
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </label>
        <label className="campo">
          <span className="etiqueta">N° de vale</span>
          <input
            aria-label="Número de vale"
            inputMode="numeric"
            value={numero}
            onChange={(e) => setNumero(e.target.value.replace(/\D/g, ''))}
          />
        </label>
      </div>
      <p className="apagado">
        Vales de venta: documentos internos (no son DTE ni van al SII). Mueven stock, caja y cuenta
        corriente como una boleta.
      </p>
      {q.isError && <PanelError error={q.error} />}
      <table className="grilla compacta" aria-label="Vales de venta">
        <thead>
          <tr>
            <th className="der">N°</th>
            <th>Fecha</th>
            <th>Cliente</th>
            <th>Motivo</th>
            <th>Nota</th>
            <th className="der">Total</th>
            <th>Emitido</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {filas.map((v) => (
            <tr key={v.id} className={v.anulado ? 'apagado' : undefined}>
              <td className="der mono">{v.numero}</td>
              <td>{fecha(v.fechaEmision)}</td>
              <td>
                {v.clienteRazonSocial}{' '}
                <span className="apagado mono">{rutVisible(v.clienteRut)}</span>
              </td>
              <td>
                {nombreMotivoVale(v.motivo)}
                {v.anulado && (
                  <>
                    {' · '}
                    <b data-vale-anulado={v.numero}>Anulado</b>
                  </>
                )}
              </td>
              <td>
                <button
                  type="button"
                  className="enlace"
                  onClick={() => alAbrirNota(v.notaId, v.notaNumeroVisible)}
                >
                  {v.notaNumeroVisible}
                </button>
              </td>
              <td className="der mono">{pesos(v.total)}</td>
              <td>{fechaHora(v.creadoEn)}</td>
              <td>
                <button type="button" onClick={() => setImprimir(v.id)}>
                  Reimprimir
                </button>
              </td>
            </tr>
          ))}
          {q.data && !filas.length && (
            <tr>
              <td colSpan={8}>Sin vales en el período.</td>
            </tr>
          )}
        </tbody>
        {filas.length > 0 && (
          <tfoot>
            <tr>
              <td colSpan={5} className="der">
                <b>
                  {vigentes.length} {vigentes.length === 1 ? 'vale' : 'vales'}
                  {filas.length > vigentes.length &&
                    ` (+ ${filas.length - vigentes.length} anulado${filas.length - vigentes.length === 1 ? '' : 's'}, fuera del total)`}
                </b>
              </td>
              <td className="der mono">
                <b>{pesos(String(total))}</b>
              </td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        )}
      </table>
      {imprimir && <ImprimirVale valeId={imprimir} alTerminar={() => setImprimir(null)} />}
    </div>
  );
}
